import React, { useState } from 'react';
import * as S from './styles';
import Frame from '../Frame';

export type Alignment = 'left' | 'right' | 'top' | 'bottom';
export type TooltipProps = {
  children: React.ReactElement;
  alignment?: Alignment;
  content: React.ReactElement | null;
  translateX?: number;
  visibility?: boolean;
  onVisibility?: (value: boolean) => void;
};

const Tooltip = ({
  content,
  children,
  alignment = 'left',
  translateX,
  visibility = false,
  onVisibility,
}: TooltipProps) => {
  const [position, setPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const [parentPosition, setParentPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const [isClicked, setIsClicked] = React.useState<boolean>(false);

  const childRef = React.useRef<any>();
  const parentRef = React.useRef<any>();
  const frameRef = React.useRef<any>();
  const [rendered, setRendered] = useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      if (childRef.current) {
        setTimeout(() => {
          const rect = childRef.current?.getBoundingClientRect();
          if (rect) {
            const { top, left, width, height } = rect;
            setPosition({ top, left, width, height });
          }
        }, 500);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  React.useEffect(() => {
    if (frameRef.current) {
      const { top, left, width, height } =
        frameRef.current.getBoundingClientRect();
      setParentPosition({ top, left, width, height });
      setRendered(true);
    }
    onVisibility && onVisibility(isClicked);
  }, [isClicked]);

  React.useEffect(() => {
    setIsClicked(visibility);
  }, [visibility]);

  const handleToggleTooltip = (e) => {
    setIsClicked(!isClicked);
  };

  const handleClickOutside = (e) => {
    if (
      !childRef?.current?.contains(e.target) &&
      !parentRef?.current?.contains(e.target) &&
      !e.target.classList.contains('react-datepicker__day')
    ) {
      setIsClicked(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isClicked]);

  return (
    <>
      <div>
        {React.cloneElement(children, {
          ref: childRef,
          onClick: handleToggleTooltip,
        })}
      </div>
      {rendered && isClicked ? (
        <S.TooltipContent
          ref={parentRef}
          alignment={alignment}
          top={position.top}
          left={position.left}
          width={position.width}
          height={position.height}
          contentWidth={parentPosition.width}
          translateX={translateX}
          visiblility={isClicked}
        >
          {content}
        </S.TooltipContent>
      ) : null}
      <Frame ref={frameRef}>
        <S.TooltipFrameContent>{content}</S.TooltipFrameContent>
      </Frame>
    </>
  );
};

export default Tooltip;
